import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import CompanySwitcher from "../CompanySwitcher";
import SubscriptionCancellingBanner from "../SubscriptionCancellingBanner";
import { isModuleEnabled } from "../../utils/permissions";
import { canUseOnDemandSigning } from "../../utils/organizationFeatures";
import {
  Squares2X2Icon,
  DocumentDuplicateIcon,
  DocumentTextIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  UserGroupIcon,
  PaperAirplaneIcon,
  RectangleStackIcon,
  KeyIcon,
  BuildingOffice2Icon,
  ChartBarIcon,
  ArrowTopRightOnSquareIcon,
} from "@heroicons/react/24/outline";
import {
  Squares2X2Icon as Squares2X2IconSolid,
  DocumentDuplicateIcon as DocumentDuplicateIconSolid,
  DocumentTextIcon as DocumentTextIconSolid,
  PaperAirplaneIcon as PaperAirplaneIconSolid,
  RectangleStackIcon as RectangleStackIconSolid,
  UserGroupIcon as UserGroupIconSolid,
  KeyIcon as KeyIconSolid,
  BuildingOffice2Icon as BuildingOffice2IconSolid,
} from "@heroicons/react/24/solid";

/** Match nav item for icon active state (includes sub-routes when `end` is false). */
function flatNavItemIsActive(pathname, item) {
  if (item.activePaths?.some((p) => pathname === p)) return true;
  if (item.end === false && item.path) {
    if (pathname === item.path) return true;
    if (pathname.startsWith(`${item.path}/`)) return true;
  }
  return false;
}

const Sidebar = ({ onCollapse, isCollapsed, setIsCollapsed }) => {
  const { user, permissions, organization } = useAuth();
  const isOrgOwner = !!user?.organizationId;
  const location = useLocation();

  const usedSignRequests = organization?.usedAllowedSignRequests ?? 0;
  const allowedSignRequests = organization?.allowedSignRequests ?? 0;
  const showOnDemandSigning = canUseOnDemandSigning(organization);
  const usagePercent =
    allowedSignRequests > 0
      ? Math.min(100, Math.round((usedSignRequests / allowedSignRequests) * 100))
      : 0;
  const usageLabel =
    allowedSignRequests < 0
      ? "Unlimited"
      : `${usedSignRequests} / ${allowedSignRequests}`;

  const [openSections, setOpenSections] = useState({});
  const [hoveredItem, setHoveredItem] = useState(null);
  const [popoverRect, setPopoverRect] = useState(null);
  const hideTimeoutRef = useRef(null);

  // Icon mapping for solid versions
  const iconMap = {
    Squares2X2Icon: { outline: Squares2X2Icon, solid: Squares2X2IconSolid },
    DocumentDuplicateIcon: {
      outline: DocumentDuplicateIcon,
      solid: DocumentDuplicateIconSolid,
    },
    DocumentTextIcon: {
      outline: DocumentTextIcon,
      solid: DocumentTextIconSolid,
    },
    PaperAirplaneIcon: {
      outline: PaperAirplaneIcon,
      solid: PaperAirplaneIconSolid,
    },
    RectangleStackIcon: {
      outline: RectangleStackIcon,
      solid: RectangleStackIconSolid,
    },
    UserGroupIcon: { outline: UserGroupIcon, solid: UserGroupIconSolid },
    KeyIcon: { outline: KeyIcon, solid: KeyIconSolid },
    BuildingOffice2Icon: {
      outline: BuildingOffice2Icon,
      solid: BuildingOffice2IconSolid,
    },
  };

  const handleCollapse = () => {
    const newCollapsedState = !isCollapsed;
    setIsCollapsed(newCollapsedState);
    onCollapse?.(newCollapsedState);
  };

  const navItems = [
    {
      path: "/dashboard",
      activePaths: ["/dashboard"],
      label: "Dashboard",
      icon: Squares2X2Icon,
      iconName: "Squares2X2Icon",
      module: "dashboard",
    },
    {
      path: "/dashboard/templates",
      activePaths: ["/dashboard/templates", "/dashboard/templates/create"],
      label: "Templates",
      icon: DocumentDuplicateIcon,
      iconName: "DocumentDuplicateIcon",
      module: "templates",
      end: false,
    },
    {
      path: "/dashboard/bulk-send",
      activePaths: ["/dashboard/bulk-send"],
      label: "Bulk Send",
      icon: PaperAirplaneIcon,
      iconName: "PaperAirplaneIcon",
      module: "bulk_send",
    },
    {
      path: "/dashboard/ondemand-signing",
      activePaths: ["/dashboard/ondemand-signing"],
      end: false,
      label: "On-demand Signing",
      icon: RectangleStackIcon,
      iconName: "RectangleStackIcon",
      featureEnabled: showOnDemandSigning,
    },
    {
      path: "/dashboard/documents",
      activePaths: [
        "/dashboard/documents",
        "/dashboard/documents/signed",
        "/dashboard/documents/pending",
        "/dashboard/documents/expired",
        "/dashboard/documents/declined",
      ],
      label: "Documents",
      icon: DocumentTextIcon,
      iconName: "DocumentTextIcon",
      module: "documents",
      end: false,
    },
    // {
    //   path: "/dashboard/self-sign",
    //   activePaths: ["/dashboard/self-sign"],
    //   label: "Self Sign",
    //   icon: AtSymbolIcon,
    //   module: null, // Self Sign is always accessible
    // },
    {
      path: "/dashboard/members",
      activePaths: ["/dashboard/members"],
      label: "Members",
      icon: UserGroupIcon,
      iconName: "UserGroupIcon",
      module: "members",
    },
    {
      path: "/dashboard/roles",
      activePaths: ["/dashboard/roles"],
      label: "Roles",
      icon: KeyIcon,
      iconName: "KeyIcon",
      module: "roles",
    },
    // {
    //   path: "/dashboard/signature",
    //   activePaths: ["/dashboard/signature"],
    //   label: "Signature",
    //   icon: PencilIcon,
    //   module: null, // Signature is always accessible
    // },
    {
      label: "Organization",
      icon: BuildingOffice2Icon,
      iconName: "BuildingOffice2Icon",
      activePaths: [
        "/dashboard/organization",
        "/dashboard/organization/subscription",
      ],
      module: null,
      orgOwnerOnly: true,
      nested: [
        {
          path: "/dashboard/organization",
          activePaths: ["/dashboard/organization"],
          label: "Companies",
        },
        {
          path: "/dashboard/organization/subscription",
          activePaths: ["/dashboard/organization/subscription"],
          label: "Subscription & Usage",
        },
      ],
    },
  ];

  useEffect(() => {
    const currentPath = location.pathname;
    const newOpenSections = {};
    setHoveredItem(null);
    setPopoverRect(null);

    navItems.forEach((item) => {
      if (item.nested) {
        const shouldBeOpen = item.nested.some(
          (nestedItem) => currentPath === nestedItem.path,
        );
        newOpenSections[item.label] = shouldBeOpen;
      }
    });

    setOpenSections(newOpenSections);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(
    () => () => {
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    },
    [],
  );

  const toggleSection = (label) => {
    setOpenSections((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };

  const handlePopoverEnter = (item, el) => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    if (el) {
      const rect = el.getBoundingClientRect();
      setPopoverRect({ top: rect.top, left: rect.right + 8 });
    }
    setHoveredItem(item);
  };

  const handlePopoverLeave = () => {
    hideTimeoutRef.current = setTimeout(() => {
      setHoveredItem(null);
      setPopoverRect(null);
    }, 150);
  };

  const handlePopoverContentEnter = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  };

  const handlePopoverContentLeave = () => {
    setHoveredItem(null);
    setPopoverRect(null);
  };

  return (
    <div
      className={`h-full bg-white flex flex-col overflow-hidden transition-all duration-300 border-r border-gray-200 ${
        isCollapsed ? "w-16" : "w-72"
      }`}
    >
      {/* Fixed header */}
      <div className="shrink-0 px-4 py-5 flex items-center justify-between">
        {!isCollapsed && (
          <Link to="/dashboard">
            <img
              className="h-auto w-[150px]"
              src="/Logo_EzSignly.png"
              alt="Company Logo"
            />
          </Link>
        )}
        <button
          onClick={handleCollapse}
          className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-900 transition-colors duration-200"
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {isCollapsed ? (
            <ChevronDoubleRightIcon className="h-5 w-5" />
          ) : (
            <ChevronDoubleLeftIcon className="h-5 w-5" />
          )}
        </button>
      </div>

      {/* Scrollable content — Company Switcher + Nav */}
      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-3 py-2 sidebar-scroll">
        <div className={isCollapsed ? "mb-4" : "mb-4 px-1"}>
          <CompanySwitcher collapsed={isCollapsed} />
        </div>

        <nav className="space-y-0.5">
          {navItems.map((item) => {
            // Check if module is enabled or if it's a profile (always accessible)
            if (item.module && !isModuleEnabled(permissions, item.module)) {
              return null;
            }
            if (item.featureEnabled === false) {
              return null;
            }
            // Only show org-owner items to org admins
            if (item.orgOwnerOnly && !isOrgOwner) {
              return null;
            }

            return (
              <div key={item.path || item.label}>
                {item.nested ? (
                  <>
                    {isCollapsed ? (
                      <div
                        onMouseEnter={(e) =>
                          handlePopoverEnter(item, e.currentTarget)
                        }
                        onMouseLeave={handlePopoverLeave}
                        className="relative"
                      >
                        <button
                          className={`w-full flex items-center justify-center p-2.5 rounded-lg transition-colors duration-200 ${
                            hoveredItem?.label === item.label
                              ? "bg-primary/10 text-primary"
                              : "text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                          }`}
                        >
                          {(() => {
                            const isActive =
                              flatNavItemIsActive(location.pathname, item) ||
                              (item.nested &&
                                item.nested.some((nested) =>
                                  nested.activePaths?.some(
                                    (path) => location.pathname === path,
                                  ),
                                ));
                            const IconComponent =
                              isActive &&
                              item.iconName &&
                              iconMap[item.iconName]
                                ? iconMap[item.iconName].solid
                                : item.icon;
                            return <IconComponent className="h-4 w-4" />;
                          })()}
                        </button>
                      </div>
                    ) : (
                      <>
                        <button
                          onClick={() => toggleSection(item.label)}
                          className={`w-full flex items-center justify-between p-2.5 rounded-lg transition-colors duration-200 ${
                            openSections[item.label]
                              ? "bg-primary/10 text-primary font-medium"
                              : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                          }`}
                        >
                          <div className="flex items-center space-x-2">
                            {(() => {
                              const isActive =
                                flatNavItemIsActive(location.pathname, item) ||
                                (item.nested &&
                                  item.nested.some((nested) =>
                                    nested.activePaths?.some(
                                      (path) => location.pathname === path,
                                    ),
                                  ));
                              const IconComponent =
                                isActive &&
                                item.iconName &&
                                iconMap[item.iconName]
                                  ? iconMap[item.iconName].solid
                                  : item.icon;
                              return (
                                <IconComponent className="h-5 w-5 shrink-0" />
                              );
                            })()}
                            <span className="text-sm font-medium">
                              {item.label}
                            </span>
                          </div>
                          {openSections[item.label] ? (
                            <ChevronUpIcon className="h-4 w-4 shrink-0 text-gray-400" />
                          ) : (
                            <ChevronDownIcon className="h-4 w-4 shrink-0 text-gray-400" />
                          )}
                        </button>

                        {openSections[item.label] && (
                          <div className="ml-7 mt-1 space-y-0.5 border-l border-gray-200 pl-3">
                            {item.nested.map((nestedItem) => (
                              <NavLink
                                key={nestedItem.path}
                                to={nestedItem.path}
                                end
                                className={({ isActive }) =>
                                  `block px-2.5 py-2 rounded-lg transition-colors duration-200 text-sm ${
                                    isActive
                                      ? "bg-primary/10 text-primary font-semibold"
                                      : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                                  }`
                                }
                              >
                                {nestedItem.label}
                              </NavLink>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </>
                ) : isCollapsed ? (
                  <div
                    onMouseEnter={(e) =>
                      handlePopoverEnter(item, e.currentTarget)
                    }
                    onMouseLeave={handlePopoverLeave}
                    className="relative"
                  >
                    <NavLink
                      to={item.path}
                      end={item.end !== false}
                      className={({ isActive }) =>
                        `flex items-center justify-center p-2.5 rounded-lg transition-colors duration-200 ${
                          hoveredItem?.label === item.label
                            ? "bg-primary/10 text-primary"
                            : isActive
                              ? "bg-primary/10 text-primary"
                              : "text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                        }`
                      }
                    >
                      {(() => {
                        const isActive = flatNavItemIsActive(
                          location.pathname,
                          item,
                        );
                        const IconComponent =
                          isActive && item.iconName && iconMap[item.iconName]
                            ? iconMap[item.iconName].solid
                            : item.icon;
                        return <IconComponent className="h-5 w-5 shrink-0" />;
                      })()}
                    </NavLink>
                  </div>
                ) : (
                  <NavLink
                    to={item.path}
                    end={item.end !== false}
                    className={({ isActive }) =>
                      `flex items-center justify-between p-2.5 rounded-lg transition-colors duration-200 ${
                        isActive
                          ? "bg-primary/10 text-primary font-medium"
                          : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                      }`
                    }
                  >
                    <div className="flex items-center gap-3">
                      {(() => {
                        const isActive = flatNavItemIsActive(
                          location.pathname,
                          item,
                        );
                        const IconComponent =
                          isActive && item.iconName && iconMap[item.iconName]
                            ? iconMap[item.iconName].solid
                            : item.icon;
                        return <IconComponent className="h-5 w-5 shrink-0" />;
                      })()}
                      <span className="text-sm font-medium">{item.label}</span>
                    </div>
                  </NavLink>
                )}
              </div>
            );
          })}
        </nav>
      </div>

      {/* Usage highlight — bottom section */}
      {!isCollapsed && (
        <div className="shrink-0 px-3 pb-4 pt-2 border-t border-gray-200">
          <SubscriptionCancellingBanner compact />
          <Link
            to="/dashboard/organization/subscription"
            className="block p-3 rounded-xl bg-primary hover:bg-primary/90 transition-colors duration-200 group"
          >
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="flex items-center gap-2">
                <ChartBarIcon className="h-5 w-5 text-white shrink-0" />
                <span className="text-sm font-semibold text-white">
                  Usage
                </span>
              </div>
              <ArrowTopRightOnSquareIcon className="h-4 w-4 text-white/80 group-hover:text-white shrink-0 transition-colors" />
            </div>
            <p className="text-lg font-bold text-white">
              {usageLabel}
            </p>
            <p className="text-xs text-white/80 mt-0.5">
              Sign requests this period
            </p>
            {allowedSignRequests > 0 && (
              <div className="mt-2 w-full bg-white/20 rounded-full h-1.5">
                <div
                  className="bg-white h-1.5 rounded-full transition-all duration-300"
                  style={{ width: `${usagePercent}%` }}
                />
              </div>
            )}
          </Link>
        </div>
      )}

      {/* Collapsed hover popover (portal) */}
      {hoveredItem &&
        popoverRect &&
        ReactDOM.createPortal(
          <div
            className="fixed z-[9999] w-40 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 py-1.5"
            style={{
              top: popoverRect.top,
              left: popoverRect.left,
            }}
            onMouseEnter={handlePopoverContentEnter}
            onMouseLeave={handlePopoverContentLeave}
          >
            {hoveredItem.nested ? (
              <div className="space-y-0.5">
                {hoveredItem.nested.map((nestedItem) => (
                  <NavLink
                    key={nestedItem.path}
                    to={nestedItem.path}
                    end
                    className={({ isActive }) =>
                      `block px-2 py-1.5 rounded-md transition-colors text-sm mx-1 ${
                        isActive
                          ? "bg-transparent text-primary font-semibold"
                          : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                      }`
                    }
                  >
                    {nestedItem.label}
                  </NavLink>
                ))}
              </div>
            ) : (
              <NavLink
                to={hoveredItem.path}
                end={hoveredItem.end !== false}
                className={({ isActive }) =>
                  `block px-2 py-1.5 rounded-md transition-colors text-sm mx-1 ${
                    isActive
                      ? "bg-transparent text-primary font-semibold"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  }`
                }
              >
                {hoveredItem.label}
              </NavLink>
            )}
          </div>,
          document.body,
        )}
    </div>
  );
};

export default Sidebar;
